import { useFormContext } from 'react-hook-form';
import { FC, useMemo } from 'react';

import { FieldLabel } from 'src/presentations/components/molecules/fields/FieldLabel';
import { formatDate } from 'src/utils/date';

import { INoContractContentStep2Props, NoContractForm } from './types';
import { NoContractSectionHeader } from './SectionHeader';
import { NoContractSection } from './Section';
import { TableInvoiceCharges } from './TableInvoiceCharges';
import { TableNewService } from './TableNewService';
import { TableSkip } from './TableSkip';
import { TableUnrecognizedService } from './TableUnrecognizedService';
import { useShipmentSelector } from '../ShipmentSelector';

const chargeTypes = [
  { key: 'declaration', label: 'Declaration Charges' },
  { key: 'shipment', label: 'Shipment Charges' },
  { key: 'container', label: 'Container Charges' },
  { key: 'weight', label: 'Weight Charges' },
  { key: 'percentage', label: 'Percentage Charges' },
];

export const NoContractContentStep3: FC<INoContractContentStep2Props> = (props) => {
  const { matchingService, draft, activeStep } = props;
  const { watch } = useFormContext<NoContractForm>();
  const {
    verdict,
    reason,
    invoiceCharges,
    newMatchingService,
    containerCharges,
    declarationCharges,
    shipmentDeclarationCharges,
    weightCharges,
    percentageCharges,
    contractChoice,
  } = watch();
  const { shipment } = useShipmentSelector();

  // Get the selected contract based on contractChoice (contractFlowId)
  const selectedContract = useMemo(() => {
    if (!contractChoice || !newMatchingService?.contractInfos) return null;
    return newMatchingService.contractInfos.find((c) => c.contractFlowId === contractChoice);
  }, [contractChoice, newMatchingService]);

  // Merge form values (chargeCategory, legType) with original shipment data
  const mergedInvoiceCharges = useMemo(() => {
    if (!shipment?.invoiceCharges) return [];

    return shipment.invoiceCharges.map((originalCharge, index) => {
      const formCharge = invoiceCharges?.[index];
      return {
        ...originalCharge,
        chargeCategory: formCharge?.chargeCategory,
        legType: formCharge?.legType,
      };
    });
  }, [shipment?.invoiceCharges, invoiceCharges]);

  return (
    <div className='p-24 border-t-1 border-[#DFDFDF]'>
      {verdict === 'reject' && (
        <div className='mt-48'>
          <NoContractSectionHeader title='Summary' description="Here's the summary" />
          <div className='flex flex-col gap-2 mt-40'>
            <FieldLabel label='Outcome'>
              <p>Reject Shipment</p>
            </FieldLabel>
          </div>
          <div className='flex flex-col gap-2 mt-24'>
            <FieldLabel label='Reason'>
              <p>{reason}</p>
            </FieldLabel>
          </div>
        </div>
      )}

      {verdict === 'accept' && (
        <div className='mt-48'>
          <NoContractSectionHeader title='Summary' description="Here's the summary" />

          {/* Shipment Details Section */}
          {matchingService?.unmatchedService && (
            <div className='mt-24'>
              <h3 className='text-16 font-semibold mb-16 text-neutral-80'>Shipment Details</h3>
              <TableUnrecognizedService data={[matchingService.unmatchedService]} mismatchedFields={[]} />
            </div>
          )}

          {/* Categorized Invoice Charges */}
          <div className='mt-24'>
            <h3 className='text-16 font-semibold mb-16 text-neutral-80'>Categorized Charges</h3>
            <TableInvoiceCharges data={mergedInvoiceCharges} isEditable={false} />
          </div>
        </div>
      )}

      {verdict === 'setup-new' && (
        <div className='mt-0'>
          {/* New Service Details */}
          {newMatchingService && (
            <div className='pb-24'>
              <h3 className='text-16 font-semibold mb-16 text-neutral-80'>New Service Details</h3>
              {contractChoice ? (
                <TableNewService data={newMatchingService} />
              ) : (
                matchingService?.unmatchedService && <TableSkip data={[matchingService.unmatchedService]} />
              )}
            </div>
          )}

          {/* New Charges Summary header (left) with Validity Period (right) */}
          <div className='flex items-start justify-between mt-24'>
            <div>
              <NoContractSectionHeader title='New Charges Summary' description='Overview of the charges that will be added to this new shipping service' />
            </div>
          </div>

          {/* Charge Count Summary */}
          <div className='mt-24 flex p-24 justify-center items-center gap-32 rounded-8 border-1 border-[#DFDFDF]'>
            {chargeTypes.map((chargeType) => {
              // Count charges from the respective arrays
              const chargeCountMap = {
                declaration: declarationCharges?.length || 0,
                shipment: shipmentDeclarationCharges?.length || 0,
                container: containerCharges?.length || 0,
                weight: weightCharges?.length || 0,
                percentage: percentageCharges?.length || 0,
              };

              return (
                <div
                  key={chargeType.key}
                  className='relative flex items-center justify-center gap-8 after:absolute after:top-0 after:right-[-12px] after:w-[1px] after:h-full after:bg-neutral-20 last:after:hidden pr-10'
                >
                  <div className='text-20 font-bold text-[#000]'>{chargeCountMap[chargeType.key]}</div>
                  <div className='text-14 text-[#9F9F9F]'>{chargeType.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
